import React from "react";
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  Slide,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  Close as CloseIcon,
  Assignment as DefaultIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import StandardButton from "./StandardButton";

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 20,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.5)'
      : '0 8px 32px rgba(0, 0, 0, 0.12)',
  },
  '& .MuiDialog-paper:not(.MuiDialog-paperFullScreen)': {
    minWidth: 650,
    maxWidth: 780,
    [theme.breakpoints.down('md')]: {
      minWidth: '90vw',
      maxWidth: '95vw',
      margin: theme.spacing(1),
    },
  }
}));

const DialogTitleStyled = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  textAlign: 'center',
  padding: theme.spacing(3, 3, 2, 3),
  position: 'relative',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const TitleIcon = styled(Box)(({ theme, iconcolor }) => ({
  backgroundColor: iconcolor || theme.palette.primary.main,
  borderRadius: '50%',
  width: 60,
  height: 60,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px auto',
  color: 'white',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  minWidth: 40,
  width: 40,
  height: 40,
  borderRadius: '50%',
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
  '&:disabled': {
    color: theme.palette.text.disabled,
  },
}));

const FullscreenButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(7),
  top: theme.spacing(1),
  minWidth: 40,
  width: 40,
  height: 40,
  borderRadius: '50%',
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.primary,
  },
  '&:disabled': {
    color: theme.palette.text.disabled,
  },
}));

const DialogContentStyled = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
  background: theme.palette.background.paper,
  position: 'relative'
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 16,
  zIndex: 10,
}));

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const StandardModalAdv = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  icon: IconComponent = DefaultIcon,
  iconColor,
  maxWidth = "md",
  fullWidth = true,
  actions = [],
  loading = false,
  loadingText = "Carregando...",
  disableEscapeKeyDown = false,
  disableBackdropClick = false,
  showCloseButton = true,
  allowFullscreen = false,
  transitionDirection = "up",
}) => {
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  const handleClose = (event, reason) => {
    if (loading) return;
    if (disableBackdropClick && reason === 'backdropClick') return;
    if (disableEscapeKeyDown && reason === 'escapeKeyDown') return;
    setIsFullscreen(false);
    onClose();
  };

  const handleActionClick = (action) => {
    if (action.disabled || action.loading) return;
    action.onClick();
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={isFullscreen}
      TransitionComponent={Slide}
      TransitionProps={{ direction: transitionDirection }}
      disableEscapeKeyDown={disableEscapeKeyDown}
    >
      <DialogTitleStyled>
        {allowFullscreen && (
          <Tooltip title={isFullscreen ? "Sair do modo tela cheia" : "Tela cheia"}>
            <FullscreenButton
              onClick={toggleFullscreen}
              disabled={loading}
              aria-label="Fullscreen"
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </FullscreenButton>
          </Tooltip>
        )}

        {showCloseButton && (
          <Tooltip title="Fechar modal">
            <CloseButton
              onClick={() => onClose()}
              disabled={loading}
              aria-label="Fechar modal"
            >
              <CloseIcon />
            </CloseButton>
          </Tooltip>
        )}

        <TitleIcon iconcolor={iconColor}>
          <IconComponent sx={{ fontSize: 32 }} />
        </TitleIcon>

        <Typography variant={isFullscreen ? "h4" : "h5"} component="h2" fontWeight={600}>
          {title}
        </Typography>

        {subtitle && (
          <Typography
            variant={isFullscreen ? "body1" : "body2"}
            color="text.secondary"
            sx={{ mt: 1 }}
          >
            {subtitle}
          </Typography>
        )}
      </DialogTitleStyled>

      <DialogContentStyled>
        {loading && (
          <LoadingOverlay>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 2 }}>
              {loadingText}
            </Typography>
          </LoadingOverlay>
        )}
        {children}
      </DialogContentStyled>

      {actions.length > 0 && (
        <DialogActions sx={{ p: 3, gap: 2 }}>
          {actions.map((action, index) => (
            <StandardButton
              key={index}
              variant={action.variant || 'outlined'}
              color={action.color || 'primary'}
              onClick={() => handleActionClick(action)}
              disabled={action.disabled || loading}
              loading={action.loading}
              startIcon={action.startIcon}
              endIcon={action.endIcon}
            >
              {action.label}
            </StandardButton>
          ))}
        </DialogActions>
      )}
    </StyledDialog>
  );
};

// ============================================================================
// PROP TYPES
// ============================================================================

StandardModalAdv.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  icon: PropTypes.elementType,
  iconColor: PropTypes.string,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
  fullWidth: PropTypes.bool,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      variant: PropTypes.oneOf(['text', 'outlined', 'contained']),
      color: PropTypes.oneOf(['inherit', 'primary', 'secondary', 'success', 'error', 'info', 'warning']),
      disabled: PropTypes.bool,
      loading: PropTypes.bool,
      startIcon: PropTypes.node,
      endIcon: PropTypes.node,
    })
  ),
  loading: PropTypes.bool,
  loadingText: PropTypes.string,
  disableEscapeKeyDown: PropTypes.bool,
  disableBackdropClick: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  allowFullscreen: PropTypes.bool,
  transitionDirection: PropTypes.oneOf(['up', 'down', 'left', 'right']),
};

export default StandardModalAdv;
